import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  FileText, Plus, Loader2, Save, X, Edit3, Trash2, Eye, EyeOff,
  Image as ImageIcon, Film, Calendar, Briefcase, PartyPopper, Tag,
  MapPin, DollarSign, Ticket, Building2, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import FileUpload from "@/components/dashboard/FileUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const typeLabels = {
  post: "Blog Post", update: "Update", promo: "Promotion",
  job: "Job Posting", event: "Event", deal: "Deal / Coupon",
};
const typeColors = {
  post: "text-[#3B82F6] bg-[#3B82F6]/10",
  update: "text-[#10B981] bg-[#10B981]/10",
  promo: "text-[#F59E0B] bg-[#F59E0B]/10",
  job: "text-[#8B5CF6] bg-[#8B5CF6]/10",
  event: "text-[#EC4899] bg-[#EC4899]/10",
  deal: "text-[#EF4444] bg-[#EF4444]/10",
  // Also handle stored types for display
  job_listing: "text-[#8B5CF6] bg-[#8B5CF6]/10",
  coupon: "text-[#EF4444] bg-[#EF4444]/10",
};
const typeIcons = {
  post: FileText, update: FileText, promo: Tag,
  job: Briefcase, event: PartyPopper, deal: Tag,
  job_listing: Briefcase, coupon: Tag,
};

const EMPTY_FORM = {
  title: "", body: "", content_type: "post", image_url: "", video_url: "", external_url: "", status: "draft",
  company_name: "", salary: "", job_location: "",
  event_date: "", venue: "", ticket_url: "",
  discount_amount: "", coupon_code: "", expiry_date: "",
};

export default function DashboardContent() {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/content`, { headers: getAuthHeader() });
      setItems(res.data?.items || []);
    } catch (err) { console.error("Failed to fetch content:", err); }
    finally { setLoading(false); }
  };

  const openNew = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setShowEditor(true);
  };

  const openEdit = (item) => {
    // Map stored content_type back to dashboard type for the form
    const dType = item.dashboard_type || item.content_type || "post";
    setForm({
      title: item.title || "",
      body: item.body || "",
      content_type: dType,
      image_url: item.image_url || "",
      video_url: item.video_url || "",
      external_url: item.external_url || "",
      status: item.status || "draft",
      company_name: item.company_name || "",
      salary: item.salary || "",
      job_location: item.job_location || "",
      event_date: item.event_date || "",
      venue: item.venue || "",
      ticket_url: item.ticket_url || "",
      discount_amount: item.discount_amount || "",
      coupon_code: item.coupon_code || "",
      expiry_date: item.expiry_date || "",
    });
    setEditingId(item.content_id);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/content/${editingId}`, form, { headers: getAuthHeader() });
        toast.success("Content updated!");
      } else {
        await axios.post(`${API}/content`, form, { headers: getAuthHeader() });
        toast.success("Content created!");
      }
      setShowEditor(false);
      await fetchContent();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/content/${id}`, { headers: getAuthHeader() });
      toast.success("Content deleted");
      await fetchContent();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const toggleStatus = async (item) => {
    const newStatus = item.status === "published" ? "draft" : "published";
    try {
      await axios.patch(`${API}/content/${item.content_id}`, { status: newStatus }, { headers: getAuthHeader() });
      const isMarketplace = ["job", "event", "deal"].includes(item.content_type);
      toast.success(newStatus === "published"
        ? `Published!${isMarketplace ? " Now visible on marketplace." : ""}`
        : "Moved to draft"
      );
      await fetchContent();
    } catch (err) { toast.error("Failed to update status"); }
  };

  const isMarketplaceType = (type) => ["job", "event", "deal", "job_listing", "coupon"].includes(type);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-content">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Content</h1>
          <p className="text-[#94A3B8] mt-1">Publish posts, updates, promotions, jobs, events & deals</p>
        </div>
        <Button onClick={openNew} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="create-content-btn">
          <Plus className="w-4 h-4 mr-2" /> New Content
        </Button>
      </div>

      {/* Editor Modal */}
      <AnimatePresence>
        {showEditor && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">{editingId ? "Edit Content" : "New Content"}</h2>
              <button onClick={() => setShowEditor(false)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              {/* Content Type Selector */}
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Content Type</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(typeLabels).map(([key, label]) => {
                    const Icon = typeIcons[key] || FileText;
                    return (
                      <button key={key} onClick={() => setForm(p => ({ ...p, content_type: key }))}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${form.content_type === key ? "bg-[#7C3AED] text-white" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`}
                        data-testid={`content-type-${key}`}>
                        <Icon className="w-3.5 h-3.5" />{label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Marketplace badge for job/event/deal */}
              {isMarketplaceType(form.content_type) && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#10B981]/10 border border-[#10B981]/30">
                  <Eye className="w-4 h-4 text-[#10B981]" />
                  <span className="text-sm text-[#10B981]">
                    When published, this will appear on the <strong>Marketplace</strong> under {form.content_type === "job" ? "Jobs" : form.content_type === "event" ? "Events" : "Deals"}.
                  </span>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Title *</label>
                <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder={form.content_type === "job" ? "e.g. Senior Marketing Manager" : form.content_type === "event" ? "e.g. Business Networking Mixer" : form.content_type === "deal" ? "e.g. 50% Off First Visit" : "Content title..."}
                  className="bg-white/5 border-white/10 text-white" data-testid="content-title" />
              </div>

              {/* === JOB-SPECIFIC FIELDS === */}
              {form.content_type === "job" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-[#8B5CF6]/5 border border-[#8B5CF6]/20 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1 flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Company</label>
                    <Input value={form.company_name} onChange={(e) => setForm(p => ({ ...p, company_name: e.target.value }))}
                      placeholder="Company name" className="bg-white/5 border-white/10 text-white" data-testid="job-company" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Salary / Pay</label>
                    <Input value={form.salary} onChange={(e) => setForm(p => ({ ...p, salary: e.target.value }))}
                      placeholder="e.g. $30/hr or $80K-$100K" className="bg-white/5 border-white/10 text-white" data-testid="job-salary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Location</label>
                    <Input value={form.job_location} onChange={(e) => setForm(p => ({ ...p, job_location: e.target.value }))}
                      placeholder="City, State" className="bg-white/5 border-white/10 text-white" data-testid="job-location" />
                  </div>
                </div>
              )}

              {/* === EVENT-SPECIFIC FIELDS === */}
              {form.content_type === "event" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-[#EC4899]/5 border border-[#EC4899]/20 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Event Date</label>
                    <Input type="date" value={form.event_date} onChange={(e) => setForm(p => ({ ...p, event_date: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white" data-testid="event-date" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Venue</label>
                    <Input value={form.venue} onChange={(e) => setForm(p => ({ ...p, venue: e.target.value }))}
                      placeholder="Venue name & address" className="bg-white/5 border-white/10 text-white" data-testid="event-venue" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1 flex items-center gap-1"><Ticket className="w-3.5 h-3.5" /> Ticket URL</label>
                    <Input value={form.ticket_url} onChange={(e) => setForm(p => ({ ...p, ticket_url: e.target.value }))}
                      placeholder="https://..." className="bg-white/5 border-white/10 text-white" data-testid="event-ticket" />
                  </div>
                </div>
              )}

              {/* === DEAL-SPECIFIC FIELDS === */}
              {form.content_type === "deal" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1 flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Discount</label>
                    <Input value={form.discount_amount} onChange={(e) => setForm(p => ({ ...p, discount_amount: e.target.value }))}
                      placeholder="e.g. 50% Off, $200 Credit" className="bg-white/5 border-white/10 text-white" data-testid="deal-discount" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1 flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Coupon Code</label>
                    <Input value={form.coupon_code} onChange={(e) => setForm(p => ({ ...p, coupon_code: e.target.value }))}
                      placeholder="e.g. SAVE50" className="bg-white/5 border-white/10 text-white" data-testid="deal-code" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Expiry Date</label>
                    <Input type="date" value={form.expiry_date} onChange={(e) => setForm(p => ({ ...p, expiry_date: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white" data-testid="deal-expiry" />
                  </div>
                </div>
              )}

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-1">
                  {form.content_type === "job" ? "Job Description" : form.content_type === "event" ? "Event Details" : form.content_type === "deal" ? "Offer Details" : "Body"}
                </label>
                <textarea value={form.body} onChange={(e) => setForm(p => ({ ...p, body: e.target.value }))}
                  placeholder="Write your content... You can include inline links in HTML, e.g. <a href='https://example.com'>read more</a>" rows={8}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#7C3AED]" data-testid="content-body" />
                <p className="text-xs text-[#64748B] mt-1">Tip: HTML is supported. Use <code className="bg-white/5 px-1 rounded">&lt;a href="..."&gt;link text&lt;/a&gt;</code> to embed inline links.</p>
              </div>

              {/* External / Backlink URL */}
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-1">
                  Read Full Article / Backlink URL <span className="text-[#64748B] font-normal">(optional)</span>
                </label>
                <Input
                  value={form.external_url}
                  onChange={(e) => setForm(p => ({ ...p, external_url: e.target.value }))}
                  placeholder="https://yourblog.com/full-article"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="content-external-url"
                />
                <p className="text-xs text-[#64748B] mt-1">Adds a primary "Read full article →" button to the post — perfect for backlinks to your own blog or source article.</p>
              </div>

              {/* Media uploads */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Featured Image</label>
                  <FileUpload category="content" accept="image/*" label="Upload Image" currentUrl={form.image_url}
                    onUploaded={(url) => setForm(p => ({ ...p, image_url: url }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Video (optional)</label>
                  <FileUpload category="content" accept="video/*" label="Upload Video" currentUrl={form.video_url}
                    onUploaded={(url) => setForm(p => ({ ...p, video_url: url }))} />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowEditor(false)} className="border-white/20 text-white hover:bg-white/5">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="save-content-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingId ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content List */}
      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item, i) => {
            const dType = item.dashboard_type || item.content_type || "post";
            const Icon = typeIcons[dType] || FileText;
            return (
              <motion.div key={item.content_id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="bg-[#0F172A] border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Icon className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
                      <h3 className="text-white font-medium truncate">{item.title}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColors[dType] || typeColors.post}`}>
                        {typeLabels[dType] || dType}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${item.status === "published" ? "text-[#10B981] bg-[#10B981]/10" : "text-[#F59E0B] bg-[#F59E0B]/10"}`}>
                        {item.status}
                      </span>
                      {isMarketplaceType(dType) && item.status === "published" && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium text-[#00E5FF] bg-[#00E5FF]/10">
                          On Marketplace
                        </span>
                      )}
                    </div>
                    {/* Type-specific meta */}
                    {(dType === "job" || dType === "job_listing") && (item.company_name || item.salary || item.job_location) && (
                      <div className="flex flex-wrap gap-3 mb-1 text-xs text-[#94A3B8]">
                        {item.company_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{item.company_name}</span>}
                        {item.salary && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{item.salary}</span>}
                        {item.job_location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.job_location}</span>}
                      </div>
                    )}
                    {(dType === "event") && (item.event_date || item.venue) && (
                      <div className="flex flex-wrap gap-3 mb-1 text-xs text-[#94A3B8]">
                        {item.event_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(item.event_date).toLocaleDateString()}</span>}
                        {item.venue && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.venue}</span>}
                      </div>
                    )}
                    {(dType === "deal" || dType === "coupon") && (item.discount_amount || item.coupon_code || item.expiry_date) && (
                      <div className="flex flex-wrap gap-3 mb-1 text-xs text-[#94A3B8]">
                        {item.discount_amount && <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{item.discount_amount}</span>}
                        {item.coupon_code && <span className="font-mono bg-white/5 px-2 py-0.5 rounded">{item.coupon_code}</span>}
                        {item.expiry_date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Exp: {new Date(item.expiry_date).toLocaleDateString()}</span>}
                      </div>
                    )}
                    {item.body && <p className="text-sm text-[#94A3B8] line-clamp-2">{item.body}</p>}
                    <p className="text-xs text-[#64748B] mt-2 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {item.created_at ? new Date(item.created_at).toLocaleDateString() : "\u2014"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => toggleStatus(item)} className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white transition-colors" title={item.status === "published" ? "Unpublish" : "Publish"}>
                      {item.status === "published" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => openEdit(item)} className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white transition-colors" data-testid={`edit-content-${item.content_id}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(item.content_id)} className="p-2 rounded-lg hover:bg-red-500/10 text-[#94A3B8] hover:text-red-400 transition-colors" data-testid={`delete-content-${item.content_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : !showEditor ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="bg-[#0F172A] border border-white/10 rounded-xl p-12 text-center">
          <FileText className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No Content Yet</h2>
          <p className="text-[#94A3B8] max-w-md mx-auto mb-6">Create blog posts, updates, promotions, job postings, events, and deals. Jobs, events & deals publish directly to the marketplace.</p>
          <Button onClick={openNew} className="bg-[#7C3AED] hover:bg-[#8B5CF6]"><Plus className="w-4 h-4 mr-2" /> Create First Post</Button>
        </motion.div>
      ) : null}
    </div>
  );
}
